import styled, { keyframes, css } from 'styled-components'
import React from 'react'

const shimmer = keyframes`
  0% {
    background-position: -800px 0;
  }
  100% {
    background-position: 800px 0;
  }
`

const pulse = keyframes`
  0%, 100% {
    opacity: 0.9;
  }
  50% {
    opacity: 0.5;
  }
`

export const SkeletonBase = styled.div<{ $width?: string; $height?: string; $radius?: string }>`
  background: linear-gradient(
    90deg,
    rgba(4, 16, 38, 0.9) 0%,
    rgba(15, 34, 70, 0.95) 20%,
    rgba(166, 193, 11, 0.08) 40%,
    rgba(15, 34, 70, 0.95) 60%,
    rgba(4, 16, 38, 0.9) 100%
  );
  background-size: 1600px 100%;
  animation: ${shimmer} 1.8s infinite linear forwards, ${pulse} 3s infinite ease-in-out;
  border-radius: ${({ $radius }) => $radius || '12px'};
  border: 1px solid rgba(255, 255, 255, 0.05);
  box-shadow: inset 0 0 15px rgba(0, 0, 0, 0.2);
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || 'auto'};
`

export const SkeletonCard = styled(SkeletonBase)`
  width: 100%;
  height: 110px;
  border-radius: 16px;
`

export const SkeletonMap = styled(SkeletonBase)`
  width: 100%;
  height: 520px;
  border-radius: 20px;
`

export const SkeletonChart = styled(SkeletonBase)`
  width: 100%;
  height: 250px;
  border-radius: 20px;
`

export const SkeletonRow = styled(SkeletonBase)`
  width: 100%;
  height: 48px;
  border-radius: 8px;
  margin-bottom: 10px;
`

export interface SkeletonProps {
  variant?: 'dashboard' | 'table' | 'cards' | 'chart' | 'default'
  rows?: number
  cardsCount?: number
}

export const Skeleton: React.FC<SkeletonProps> = ({
  variant = 'dashboard',
  rows = 8,
  cardsCount = 4,
}) => {
  return (
    <ContainerSkeleton>
      {/* Header com indicador elegante */}
      <HeaderSkeleton>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <SkeletonBase $width='220px' $height='28px' $radius='6px' />
          <SkeletonBase $width='110px' $height='24px' $radius='12px' />
        </div>
        <SkeletonBase $width='160px' $height='18px' $radius='4px' style={{ marginTop: '10px' }} />
      </HeaderSkeleton>

      {/* Variantes de layout */}
      {variant === 'table' && (
        <TableLayoutSkeleton>
          {/* Barra de filtros / busca */}
          <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <SkeletonBase $width='260px' $height='44px' $radius='10px' />
            <SkeletonBase $width='180px' $height='44px' $radius='10px' />
            <SkeletonBase $width='180px' $height='44px' $radius='10px' />
          </div>

          {/* Cabeçalho da tabela */}
          <SkeletonBase $width='100%' $height='44px' $radius='10px' style={{ marginBottom: '12px' }} />

          {/* Linhas da tabela */}
          {Array.from({ length: rows }).map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </TableLayoutSkeleton>
      )}

      {variant === 'cards' && (
        <CardsGridSkeleton>
          {Array.from({ length: cardsCount || 6 }).map((_, i) => (
            <SkeletonCard key={i} style={{ height: '140px' }} />
          ))}
        </CardsGridSkeleton>
      )}

      {(variant === 'dashboard' || variant === 'default') && (
        <GridSkeleton>
          <div className='left'>
            <RowSkeleton>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </RowSkeleton>
            <SkeletonMap />
          </div>
          <div className='right'>
            <SkeletonChart style={{ marginBottom: '20px' }} />
            <SkeletonChart />
          </div>
        </GridSkeleton>
      )}
    </ContainerSkeleton>
  )
}

const ContainerSkeleton = styled.div`
  padding: 15px 20px 40px 20px;
  min-height: 85vh;
  width: 100%;
  box-sizing: border-box;
  animation: fadeIn 0.3s ease-in-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(4px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`

const HeaderSkeleton = styled.div`
  margin-bottom: 25px;
  border-left: 4px solid rgba(166, 193, 11, 0.6);
  padding-left: 15px;
`

const GridSkeleton = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  @media (min-width: 1280px) {
    grid-template-columns: 1.8fr 1fr;
  }
`

const RowSkeleton = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
`

const TableLayoutSkeleton = styled.div`
  width: 100%;
  background: rgba(0, 24, 66, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 20px;
  box-sizing: border-box;
`

const CardsGridSkeleton = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  width: 100%;
`

export default Skeleton
